import { AlertCircle } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { FinancialStatCard } from '@/components/custom/companies/overview/financial-stat-card'
import { ActualVsBudgetCardContainer } from '@/components/custom/companies/overview/actual-vs-budget-card'
import {
  type FinancialMetricCardKey,
  type FinancialMetricCardSelection,
  useCompanyMetricLabelOptions,
  useCompanyMetricCards,
} from '@/hooks/use-metrics'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import {
  applyMetricCardSelection,
  DEFAULT_FINANCIAL_METRIC_CARD_LABELS,
  EMPTY_FINANCIAL_METRIC_CARD_SELECTION,
  FINANCIALS_CARD_ORDER,
  OVERVIEW_CARD_ORDER,
} from '@/features/company-overview/metric-card-model'

interface FinancialStatCardsProps {
  companyId: string
  surface?: 'overview' | 'financials'
  showActualVsBudget?: boolean
}

export function FinancialStatCards({
  companyId,
  surface = 'financials',
  showActualVsBudget = false,
}: FinancialStatCardsProps) {
  const [selection, setSelection] = useState<FinancialMetricCardSelection>(
    () => EMPTY_FINANCIAL_METRIC_CARD_SELECTION
  )
  const cardOrder =
    surface === 'overview' ? OVERVIEW_CARD_ORDER : FINANCIALS_CARD_ORDER

  const { data: metricOptions = [] } = useCompanyMetricLabelOptions(companyId)
  const metricOptionById = useMemo(
    () => new Map(metricOptions.map((opt) => [opt.id, opt])),
    [metricOptions]
  )

  const defaultMetricIdByCard = useMemo(() => {
    const map: Partial<Record<FinancialMetricCardKey, string>> = {}
    for (const key of cardOrder) {
      const defaultLabeledAs = DEFAULT_FINANCIAL_METRIC_CARD_LABELS[key]
      const defaultOpt = metricOptions.find(
        (opt) => opt.labeledAs === defaultLabeledAs
      )
      if (defaultOpt) map[key] = defaultOpt.id
    }
    return map
  }, [cardOrder, metricOptions])

  const handleSelectMetric = useCallback(
    (cardKey: FinancialMetricCardKey, metricLabelId: string) => {
      setSelection((prev) =>
        applyMetricCardSelection(
          prev,
          cardKey,
          metricLabelId,
          defaultMetricIdByCard
        )
      )
    },
    [defaultMetricIdByCard]
  )

  const querySelection = useMemo(() => {
    if (surface !== 'overview') return selection

    return {
      ...selection,
      cash: selection.cash ?? defaultMetricIdByCard.cash ?? null,
    }
  }, [defaultMetricIdByCard, selection, surface])

  const { cards, status, isError } = useCompanyMetricCards(
    companyId,
    querySelection,
    cardOrder
  )

  if (isError) {
    return (
      <div className="flex h-40 items-center justify-center rounded-sm border border-border bg-white">
        <div className="flex items-center gap-2 text-primary/70">
          <AlertCircle className="h-4 w-4" />
          <Typography variant="body-sm">
            Failed to load financial metrics
          </Typography>
        </div>
      </div>
    )
  }

  const allSelectedMetricLabelIds = cardOrder
    .map((cardKey) => querySelection[cardKey] ?? cards[cardKey]?.metricLabelId)
    .filter((id): id is string => Boolean(id))
  const cardCount =
    cardOrder.length + (surface === 'overview' && showActualVsBudget ? 1 : 0)

  return (
    <div className="@container">
      <div
        className={cn(
          'grid grid-cols-1 gap-2 @[31rem]:grid-cols-2',
          cardCount === 3 ? '@[62rem]:grid-cols-3' : '@[62rem]:grid-cols-4'
        )}
      >
        {cardOrder.map((key) => {
          const metric = cards[key]
          const selectedMetricLabelId =
            querySelection[key] ?? metric?.metricLabelId
          const selectedOption = selectedMetricLabelId
            ? metricOptionById.get(selectedMetricLabelId)
            : undefined
          const defaultLabeledAs = DEFAULT_FINANCIAL_METRIC_CARD_LABELS[key]
          const defaultOption = metricOptions.find(
            (opt) => opt.labeledAs === defaultLabeledAs
          )

          const title =
            selectedOption?.label ??
            (metric?.metricLabelId
              ? metricOptionById.get(metric.metricLabelId)?.label
              : undefined) ??
            metricOptions.find((opt) => opt.labeledAs === metric?.labeledAs)
              ?.label ??
            defaultOption?.label ??
            metric?.labeledAs ??
            defaultLabeledAs

          const disabledMetricLabelIds = allSelectedMetricLabelIds.filter(
            (id) => id !== selectedMetricLabelId
          )

          return (
            <FinancialStatCard
              key={key}
              companyId={companyId}
              title={title}
              selectedMetricLabelId={selectedMetricLabelId ?? undefined}
              disabledMetricLabelIds={disabledMetricLabelIds}
              metricOptions={metricOptions}
              onSelectMetric={(metricLabelId) =>
                handleSelectMetric(key, metricLabelId)
              }
              value={metric?.latestValue ?? null}
              changePercent={metric?.changePercent ?? null}
              changeDisplay={metric?.changeDisplay ?? 'UNAVAILABLE'}
              comparisonQuarter={metric?.comparisonQuarter ?? null}
              comparisonValue={metric?.comparisonValue ?? null}
              points={metric?.points ?? []}
              isLoading={status[key].isLoading}
            />
          )
        })}
        {surface === 'overview' && showActualVsBudget ? (
          <ActualVsBudgetCardContainer companyId={companyId} />
        ) : null}
      </div>
    </div>
  )
}
